import React, { useEffect, useState, useRef } from "react";
import feather from "feather-icons";
import Chart from "chart.js/auto";
import { ethers } from "ethers";

// ✅ Variables d’environnement compatibles CRA
const ADMIN_WALLET = (process.env.REACT_APP_ADMIN_WALLET || "").toLowerCase();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
const TOKEN_SYMBOL = process.env.REACT_APP_TOKEN_SYMBOL || "NEXORA";
const TOKEN_ADDRESS =
  process.env.REACT_APP_TOKEN_ADDRESS ||
  "0xeD4370176558Be01cd01428b8e6842d5a0b67Ea8";
const TOKEN_PRICE = 0.0005;

// ✅ ABI minimal ERC-20 pour lecture du solde
const ERC20_ABI = [
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)",
  "function balanceOf(address) view returns (uint256)",
];

export default function Dashboard() {
  const [wallet, setWallet] = useState(localStorage.getItem("wallet-address") || "");
  const [token, setToken] = useState(localStorage.getItem("NEXORA-token") || "");
  const [userData, setUserData] = useState({ balance: 0, rewards: 0 });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const balanceChartRef = useRef(null);
  const votingChartRef = useRef(null);
  const balanceChart = useRef(null);
  const votingChart = useRef(null);

  // === Initialisation ===
  useEffect(() => {
    feather.replace();
    initWallet();
  }, []);

  // === Connexion MetaMask + lecture du solde ===
  async function initWallet() {
    try {
      if (!window.ethereum) {
        alert("🦊 Please install MetaMask!");
        setLoading(false);
        return;
      }

      const provider = new ethers.BrowserProvider(window.ethereum);
      const accounts = await provider.send("eth_requestAccounts", []);
      if (!accounts.length) {
        alert("⚠️ Please connect your wallet.");
        setLoading(false);
        return;
      }

      const address = accounts[0];
      localStorage.setItem("wallet-address", address);
      setWallet(address);

      // Auth utilisateur (signature)
      let t = localStorage.getItem("NEXORA-token");
      if (!t) {
        const signer = await provider.getSigner();
        const message = `NEXORA_USER_LOGIN_${new Date().toISOString()}`;
        const signature = await signer.signMessage(message);
        t = btoa(signature);
        localStorage.setItem("NEXORA-token", t);
      }
      setToken(t);

      await loadDashboardData(provider, address);
    } catch (err) {
      console.error("Wallet init failed:", err);
      setLoading(false);
    }
  }

  // === Lecture du solde on-chain + activité backend ===
  async function loadDashboardData(provider, address) {
    try {
      const contract = new ethers.Contract(TOKEN_ADDRESS, ERC20_ABI, provider);
      const decimals = await contract.decimals();
      const balanceRaw = await contract.balanceOf(address);
      const balance = parseFloat(ethers.formatUnits(balanceRaw, decimals));

      let activityData = [];
      try {
        const res = await fetch(`${BACKEND_URL}/api/activity`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) activityData = await res.json();
      } catch {
        console.warn("⚠️ Aucun backend ou endpoint /api/activity trouvé");
      }

      setUserData({ balance, rewards: balance * 0.01 });
      setActivity(activityData);
      updateBalanceChart(balance);
      updateVotingChart({ for: 5, against: 2, abstain: 1 });
    } catch (err) {
      console.error("Error loading dashboard data:", err);
    } finally {
      setLoading(false);
    }
  }

  // === Graphiques ===
  function updateBalanceChart(balance) {
    const ctx = balanceChartRef.current?.getContext("2d");
    if (!ctx) return;
    balanceChart.current?.destroy();

    const labels = ["T-4", "T-3", "T-2", "T-1", "Now"];
    const values = [balance * 0.7, balance * 0.8, balance * 0.9, balance * 0.95, balance];

    balanceChart.current = new Chart(ctx, {
      type: "line",
      data: {
        labels,
        datasets: [
          {
            label: `${TOKEN_SYMBOL} Balance`,
            data: values,
            borderColor: "#ec4899",
            backgroundColor: "rgba(236,72,153,0.1)",
            tension: 0.4,
            fill: true,
          },
        ],
      },
      options: { responsive: true, plugins: { legend: { display: false } } },
    });
  }

  function updateVotingChart(data) {
    const ctx = votingChartRef.current?.getContext("2d");
    if (!ctx) return;
    votingChart.current?.destroy();

    votingChart.current = new Chart(ctx, {
      type: "bar",
      data: {
        labels: ["For", "Against", "Abstain"],
        datasets: [
          {
            data: [data.for, data.against, data.abstain],
            backgroundColor: [
              "rgba(59,130,246,0.8)",
              "rgba(236,72,153,0.8)",
              "rgba(192,38,211,0.8)",
            ],
          },
        ],
      },
      options: { responsive: true, plugins: { legend: { display: false } } },
    });
  }

  useEffect(() => {
    return () => {
      balanceChart.current?.destroy();
      votingChart.current?.destroy();
    };
  }, []);

  // === Déconnexion ===
  function logout() {
    localStorage.removeItem("NEXORA-token");
    localStorage.removeItem("wallet-address");
    setWallet("");
    window.location.href = "/";
  }

  // === Action admin ===
  async function handleAirdrop() {
    if (wallet.toLowerCase() !== ADMIN_WALLET) {
      alert("⛔ Only admin can perform airdrop.");
      return;
    }
    alert("✅ Airdrop simulated (admin only).");
  }

  // === États visuels ===
  if (loading)
    return (
      <div className="flex justify-center items-center h-screen text-white">
        <p className="text-xl animate-pulse">Connecting wallet...</p>
      </div>
    );

  if (!wallet)
    return (
      <div className="flex flex-col justify-center items-center h-screen text-white gap-4">
        <p className="text-xl animate-pulse">🦊 Please connect your MetaMask wallet</p>
        <button
          onClick={initWallet}
          className="px-4 py-2 bg-gradient-to-r from-neonPink to-neonBlue rounded-lg font-semibold"
        >
          Connect Wallet
        </button>
      </div>
    );

  // === Rendu principal ===
  return (
    <div className="text-white p-6">
      {/* HEADER */}
      <div className="flex justify-between mb-8">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-neonPink to-neonBlue bg-clip-text text-transparent">
          NEXORA Dashboard
        </h1>
        <div className="flex gap-3 items-center">
          <span className="bg-gray-800 px-4 py-2 rounded-full text-sm">
            {wallet.slice(0, 6)}...{wallet.slice(-4)}
          </span>
          <button
            onClick={logout}
            className="px-3 py-2 bg-red-600 rounded-lg hover:bg-red-700 text-sm"
          >
            Logout
          </button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid md:grid-cols-4 gap-6 mb-10">
        <StatCard
          title={`${TOKEN_SYMBOL} Balance`}
          color="text-neonPink border-neonPink/40"
          value={`${userData.balance.toLocaleString()} ${TOKEN_SYMBOL}`}
          subtitle={`≈ $${(userData.balance * TOKEN_PRICE).toFixed(2)}`}
        />
        <StatCard
          title="Voting Power"
          color="text-neonBlue border-neonBlue/40"
          value={`${Math.floor(userData.balance / 1000)} VP`}
          subtitle="1 VP per 1,000 tokens"
        />
        <StatCard
          title="Rewards Earned"
          color="text-neonPurple border-neonPurple/40"
          value={`${(userData.rewards || 0).toFixed(2)} ${TOKEN_SYMBOL}`}
          subtitle="From staking & activity"
        />
        <StatCard
          title="Admin Actions"
          color="text-neonPink border-neonPink/40"
          value={wallet.toLowerCase() === ADMIN_WALLET ? "✅ Enabled" : "🔒 Locked"}
          subtitle="Only for admin"
        />
      </div>

      {/* CHARTS */}
      <div className="grid md:grid-cols-2 gap-6 mb-10">
        <ChartCard title={`${TOKEN_SYMBOL} Balance History`}>
          <canvas ref={balanceChartRef}></canvas>
        </ChartCard>
        <ChartCard title="Voting Activity">
          <canvas ref={votingChartRef}></canvas>
        </ChartCard>
      </div>

      {/* QUICK ACTIONS */}
      <div className="grid md:grid-cols-3 gap-6">
        <QuickAction
          title="Admin Airdrop"
          color="from-neonBlue to-neonPink"
          onClick={handleAirdrop}
        />
        <QuickAction
          title="Stake (soon)"
          color="from-neonPurple to-neonBlue"
          onClick={() => alert("⏳ Coming soon")}
        />
        <QuickAction
          title="Transfer (soon)"
          color="from-neonPink to-neonPurple"
          onClick={() => alert("⏳ Coming soon")}
        />
      </div>
    </div>
  );
}

// === Sous-composants ===
const StatCard = ({ title, value, subtitle, color }) => (
  <div className={`bg-gray-900 p-6 rounded-xl border ${color} shadow-lg`}>
    <h3 className="text-gray-400 mb-2">{title}</h3>
    <div className={`text-3xl font-bold ${color.split(" ")[0]}`}>{value}</div>
    <div className="text-sm text-gray-400 mt-2">{subtitle}</div>
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="bg-gray-900 p-6 rounded-xl border border-neonBlue/40 shadow-lg">
    <h2 className="text-xl font-bold mb-4">{title}</h2>
    <div className="h-64">{children}</div>
  </div>
);

const QuickAction = ({ title, onClick, color }) => (
  <div className="bg-gray-900 p-6 rounded-xl border border-gray-800 shadow-lg">
    <h2 className="text-xl font-bold mb-4">{title}</h2>
    <button
      onClick={onClick}
      className={`w-full py-2 bg-gradient-to-r ${color} rounded-lg font-semibold hover:opacity-90`}
    >
      {title}
    </button>
  </div>
);
